import React from 'react';
import {
  AppBar,
  Box,
  Drawer,
  Grid,
  IconButton,
  Stack,
  Switch,
  Toolbar,
  Tooltip
} from '@mui/material';
import PropTypes from 'prop-types';
import { styled, useTheme } from '@mui/material/styles';
import { isEqual, isEmpty } from 'lodash';
import uuid from 'uuid/v1';

import AddIcon from '@mui/icons-material/Add';
import AddLinkIcon from '@mui/icons-material/AddLink';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import CoPresentIcon from '@mui/icons-material/CoPresent';
import DescriptionIcon from '@mui/icons-material/Description';
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import EditLocationAltIcon from '@mui/icons-material/EditLocationAlt';
import ExploreIcon from '@mui/icons-material/Explore';
import ImageIcon from '@mui/icons-material/Image';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LayersIcon from '@mui/icons-material/Layers';
import MapsHomeWorkRoundedIcon from '@mui/icons-material/MapsHomeWorkRounded';
import RemoveIcon from '@mui/icons-material/Remove';
import ShareIcon from '@mui/icons-material/Share';
import StorageIcon from '@mui/icons-material/Storage';
import TableRowsIcon from '@mui/icons-material/TableRows';
import TextSnippetOutlinedIcon from '@mui/icons-material/TextSnippetOutlined';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import AnnotationDirectButtonsComponent from '../AnnotationDirectButtonsComponent';
import FileToolsComponent from '../FileToolsComponent';
import InfoLayerSelection from '../InfoLayerSelection';
import IntroductionMapComponent from '../IntroductionMapComponent';
import ISLComponent from '../ISLComponent';
import CatastroComponent from '../CatastroComponent';
// import LeafletFunctionalities from '../LeafletFunctionalities';
import DownloadLinksComponent from '../DownloadLinksComponent';
import MapLayerOrder from '../MapLayerOrder';
import MapShareTogether from '../MapShareTogether';
import LocationComplexComponent from '../LocationComplexComponent';
import LocationMediumComponent from '../LocationMediumComponent';
import LocationSimpleComponent from '../LocationSimpleComponent';
import NotesComponent from '../NotesComponent';
import SentinelComponent from '../SentinelComponent';
// import ShowMapDeckLidarComponent from '../ShowMapDeckLidarComponent';
import PointMapComponent from '../PointMapComponent';
import PythonIcon from './PythonIcon';
import PythonComponent from '../PythonComponent';
import ViewLinksComponent from '../ViewLinksComponent';
import TableAnnotationsComponent from '../TableAnnotationsComponent';
import TableSourceComponent from '../TableSourceComponent';

import useTimeStore from '../ZustandProvider';
import { useStyles } from '../../../styles/mapStylesCustom';

import { LIBRARY_ICON_TYPES } from '../../../api/AnnotationTemplates/AnnotationTemplates';

const BotonMenu = props => {
  const { changeLoading, isMobile, isPublicMap, map, MapProps, userProps } = props;
  const {
    buttonSettings,
    components,
    notes,
    orderOfComponents,
    locationSettings
  } = map.settingsAdvancedMap;

  const { advancedComponents } = MapProps.userControlSetting;

  const adjustMap = useTimeStore(state => state.adjustMap);
  const drawerWidth = useTimeStore(state => state.drawerWidth);
  const layers = useTimeStore(state => state.layers);
  const setAdjustMap = useTimeStore(state => state.setAdjustMap);
  const setDrawerWidth = useTimeStore(state => state.setDrawerWidth);
  const setGeometryConfig = useTimeStore(state => state.setGeometryConfig);

  const openLeftStart = drawerWidth !== 0;
  const filteredComponents = Object.keys(components).filter(key => components[key].show === true);
  const orderInitial =
    orderOfComponents && !isEmpty(orderOfComponents) ? orderOfComponents : filteredComponents;

  const [componentToRender, setComponentToRender] = React.useState('');
  const drawerWidthLeft =
    drawerWidth === 0 ? `${100 / 3 - 1 / 3}vw` : `${drawerWidth - drawerWidth / 100}vw`;
  const drawerWidthLocal = isMobile ? '100%' : drawerWidthLeft;
  const [elementToRenderAux, setElementToRenderAux] = React.useState(
    map.settingsAdvancedMap.defaultComponent
  );
  const [expandDrawerMobile, setExpandDrawerMobile] = React.useState(false);
  const [featureProp, setFeatureProp] = React.useState({});
  const [openLeft, setOpenLeft] = React.useState(openLeftStart);
  const [tiempoEspera, setTiempoEspera] = React.useState(false);
  const [checkAdjustMap, setCheckAdjustMap] = React.useState(true);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [statesComponent, setStatesComponent] = React.useState({
    AnnotationDirectButtons: {},
    CatastroComponent: {},
    FileToolsComponent: {},
    IntroductionMapComponent: {},
    ISLComponent: {},
    DownloadLinksComponent: {},
    InfoLayerSelection: {},
    LayersControl: {},
    LocationComponent: {},
    SentinelComponent: {},
    NotesComponent: {
      totalAnnotations: 0,
      prevIndexAnnotation: -1,
      prevVideo: { video: {}, index: -1 }
    },
    SharedComponent: {},
    ShowMapDeckLidarComponent: {},
    TableAnnotationsComponent: {},
    TableSourceComponent: {},
    PointMapComponent: {},
    PythonComponent: {},
    ViewLinksComponent: {}
  });

  const heightLeft = isPublicMap ? 0 : '56px';
  const heightMobile = openLeft ? heightLeft : '60%';
  const heightLocal = isMobile ? heightMobile : heightLeft;

  const theme = useTheme();
  const classes = useStyles();

  const marks = [
    {
      value: 25
    },
    {
      value: 100 / 3
    },
    {
      value: 50
    },
    {
      value: 75
    },
    {
      value: 100
    }
  ].sort((a, b) => a.value - b.value);

  const AppBarStyled = styled(AppBar, {
    shouldForwardProp: prop => prop !== 'open'
  })(({ open }) => ({
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    marginLeft: 0,
    ...(open && {
      marginLeft: `${drawerWidthLocal}px`,
      transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen
      })
    })
  }));

  const DrawerHeader = styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: 'center'
  }));

  document.getElementsByTagName('body')[0].onclick = () => {
    const property = JSON.parse(localStorage.getItem('Propperties'));
    if (!isEqual(property, featureProp) && property !== null) {
      setFeatureProp({ ...property });
    }
    localStorage.removeItem('Propperties');
  };

  const tiempoEsperaFunction = () => {
    setTiempoEspera(true);
    setTimeout(() => {
      setTiempoEspera(false);
    }, 500);
  };

  const handleDrawerBottom = () => {
    setIsModalOpen(false);
    if (drawerWidth === 0) setDrawerWidth(100 / 3);
    if (openLeft === true) {
      setOpenLeft(false);
      setAdjustMap(false);
    } else {
      setOpenLeft(true);
      if (checkAdjustMap === true) {
        setAdjustMap(true);
      }
    }
  };

  const handleIncrement = () => {
    const currentIndex = marks.findIndex(mark => mark.value === drawerWidth);
    if (currentIndex < marks.length - 1) {
      setDrawerWidth(marks[currentIndex + 1].value);
    }
  };

  const handleDecrement = () => {
    const currentIndex = marks.findIndex(mark => mark.value === drawerWidth);
    if (currentIndex > 0) {
      setDrawerWidth(marks[currentIndex - 1].value);
    }
  };

  const renderButtons = component => {
    const settingsComponent = components[component];
    if (isEmpty(settingsComponent.libraryIcon)) {
      if (component === 'AnnotationDirectButtons') {
        return <EditLocationAltIcon />;
      }
      if (component === 'CatastroComponent') {
        return <MapsHomeWorkRoundedIcon />;
      }
      if (component === 'FileToolsComponent') {
        return <DriveFolderUploadOutlinedIcon />;
      }
      if (component === 'TableAnnotationsComponent') {
        return <DriveFolderUploadOutlinedIcon />;
      }
      if (component === 'IntroductionMapComponent') {
        return <CoPresentIcon />;
      }
      if (component === 'ISLComponent') {
        return <StorageIcon />;
      }
      if (component === 'DownloadLinksComponent') {
        return <AddLinkIcon />;
      }
      if (component === 'InfoLayerSelection') {
        return <InfoOutlinedIcon />;
      }
      if (component === 'LayersControl') {
        return <LayersIcon />;
      }
      if (component === 'LocationComponent') {
        return <ExploreIcon />;
      }
      if (component === 'SentinelComponent') {
        return <ImageIcon />;
      }
      if (component === 'NotesComponent') {
        return <TextSnippetOutlinedIcon />;
      }
      if (component === 'PointMapComponent') {
        return <TravelExploreIcon />;
      }
      if (component === 'PythonComponent') {
        return <PythonIcon fontSize="large" style={{ color: '#2A3F90' }} />;
      }
      if (component === 'SharedComponent') {
        return <ShareIcon />;
      }
      // if (component === 'ShowMapDeckLidarComponent') {
      //   return <MapIcon />;
      // }
      if (component === 'TableSourceComponent') {
        return <TableRowsIcon />;
      }
      if (component === 'ViewLinksComponent') {
        return <DescriptionIcon />;
      }
    } else {
      if (settingsComponent.libraryIcon === LIBRARY_ICON_TYPES.FONTAWESOME) {
        return <FontAwesomeIcon icon={settingsComponent.iconFontawesome} />;
      }
      if (settingsComponent.libraryIcon === LIBRARY_ICON_TYPES.SVG) {
        return (
          <img
            src={settingsComponent.iconSvg}
            alt={settingsComponent.iconFontawesome}
            style={{ marginRight: '5px', width: '20px', height: '20px' }}
          />
        );
      }
    }

    return '';
  };

  const renderComponent = async component => {
    switch (component) {
      case 'AnnotationDirectButtons':
        setGeometryConfig('component');
        setComponentToRender(
          <AnnotationDirectButtonsComponent
            isMobile={isMobile}
            isPublicMap={isPublicMap}
            MapProps={MapProps}
            userProps={userProps}
            mapProp={{ ...map }}
          />
        );
        break;
      case 'CatastroComponent':
        setGeometryConfig('Catastro');
        setComponentToRender(
          <CatastroComponent MapProps={MapProps} isMobile={isMobile} isFullScreen={isModalOpen} />
        );
        break;
      case 'DownloadLinksComponent':
        setGeometryConfig('');
        setComponentToRender(<DownloadLinksComponent isMobile={isMobile} MapProps={MapProps} />);
        break;
      case 'FileToolsComponent':
        setGeometryConfig('component');
        setComponentToRender(<FileToolsComponent MapProps={MapProps} />);
        break;
      case 'ISLComponent':
        setGeometryConfig('StreetView');
        setComponentToRender(
          <ISLComponent MapProps={MapProps} isMobile={isMobile} isFullScreen={isModalOpen} />
        );
        break;
      case 'InfoLayerSelection':
        setGeometryConfig('component');
        setComponentToRender(<InfoLayerSelection featureProp={featureProp} map={{ ...map }} />);
        break;
      case 'IntroductionMapComponent':
        setGeometryConfig('component');
        setComponentToRender(
          <IntroductionMapComponent MapProps={MapProps} changeLoading={changeLoading} />
        );
        break;
      case 'LayersControl':
        setGeometryConfig('component');
        setComponentToRender(
          <MapLayerOrder MapProps={MapProps} isPublicMap={isPublicMap} isMobile={isMobile} />
        );
        break;
      case 'LocationComponent':
        if (locationSettings.typeComponent === 'Simple') {
          setComponentToRender(
            <LocationSimpleComponent
              MapProps={MapProps}
              isPublicMap={isPublicMap}
              isMobile={isMobile}
              locationSettings={locationSettings}
              userProps={userProps}
            />
          );
        } else if (locationSettings.typeComponent === 'Complex') {
          setComponentToRender(
            <LocationComplexComponent
              MapProps={MapProps}
              isPublicMap={isPublicMap}
              isMobile={isMobile}
              buttonSettings={buttonSettings}
              locationSettings={locationSettings}
              userProps={userProps}
            />
          );
          setGeometryConfig('LocationComponent');
        } else {
          setComponentToRender(
            <LocationMediumComponent
              MapProps={MapProps}
              isPublicMap={isPublicMap}
              isMobile={isMobile}
              buttonSettings={buttonSettings}
              locationSettings={locationSettings}
              userProps={userProps}
            />
          );
          setGeometryConfig('LocationComponent');
        }
        break;
      case 'NotesComponent':
        setGeometryConfig('component');
        setComponentToRender(
          <NotesComponent
            MapProps={MapProps}
            userProps={userProps}
            isAnnotationsMaps
            isMobile={isMobile}
            notesProps={notes}
            isPublicMap={isPublicMap}
            previousStates={statesComponent.NotesComponent}
            saveStates={newStates =>
              setStatesComponent({ ...statesComponent, NotesComponent: newStates })
            }
          />
        );
        break;
      case 'PointMapComponent':
        setGeometryConfig('StreetView');
        setComponentToRender(<PointMapComponent MapProps={MapProps} />);
        break;
      case 'PythonComponent':
        setGeometryConfig('component');
        setComponentToRender(
          <PythonComponent pythonSettings={map.settingsAdvancedMap.pythonSettings} />
        );
        break;
      case 'SharedComponent':
        setGeometryConfig('component');
        setComponentToRender(
          <MapShareTogether
            map={{ ...map }}
            isPublicMap={isPublicMap}
            shareOptions={map.settingsAdvancedMap.shareOptions}
          />
        );
        break;
      case 'SentinelComponent':
        setGeometryConfig('sentinel');
        setComponentToRender(<SentinelComponent MapProps={MapProps} />);
        break;
      // case 'ShowMapDeckLidarComponent':
      //   setGeometryConfig('component');
      //   setComponentToRender(<ShowMapDeckLidarComponent />);
      //   break;
      case 'TableAnnotationsComponent':
        setGeometryConfig('component');
        setComponentToRender(
          <TableAnnotationsComponent tableAnnotations={map.settingsAdvancedMap.tableAnnotations} />
        );
        break;
      case 'TableSourceComponent':
        setGeometryConfig('component');
        setComponentToRender(
          <TableSourceComponent tableSource={map.settingsAdvancedMap.tableSource} />
        );
        break;

      case 'ViewLinksComponent':
        setGeometryConfig('component');
        setComponentToRender(<ViewLinksComponent isPublicMap={isPublicMap} MapProps={MapProps} />);
        break;
      default:
        setComponentToRender(false);
        break;
    }
    setElementToRenderAux(component);
  };

  React.useEffect(() => {
    if (isEmpty(layers)) {
      setTimeout(() => {
        renderComponent(elementToRenderAux);
      }, 200);
    } else {
      renderComponent(elementToRenderAux);
    }
  }, [featureProp, map.shared, map.sharedUser, map.sharedPrivate, map.sharedPrivateUser]);

  return (
    <>
      <AppBarStyled
        position="relative"
        open={openLeft}
        style={{
          backgroundColor: 'rgb(251, 192, 45, 0)',
          color: 'black',
          radio: 3
        }}
        elevation={0}
      >
        <Toolbar disableGutters>
          <Stack direction="row" justifyContent="flex-start">
            {isMobile === false && openLeft === false && (
              <IconButton
                variant="contained"
                style={{
                  backgroundColor: 'white',
                  color: '#2A3F90',
                  border: '1px solid',
                  borderColor: '#2A3F90'
                }}
                size="small"
                aria-label="open drawer"
                onClick={handleDrawerBottom}
                edge="start"
              >
                <ChevronRightIcon />
              </IconButton>
            )}
            {isMobile === true && openLeft === false && (
              <IconButton
                variant="contained"
                style={{
                  backgroundColor: 'white',
                  color: '#2A3F90',
                  border: '1px solid',
                  borderColor: '#2A3F90'
                }}
                size="small"
                aria-label="open drawer"
                onClick={handleDrawerBottom}
                edge="start"
              >
                <KeyboardArrowUpIcon />
              </IconButton>
            )}
          </Stack>
        </Toolbar>
      </AppBarStyled>
      <Drawer
        sx={{
          width: drawerWidthLocal,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidthLocal,
            boxSizing: 'border-box',
            height:
              isMobile === true && expandDrawerMobile !== true
                ? '50%'
                : `calc(100% - ${heightLeft})`,
            top: isMobile === true && expandDrawerMobile === false ? '50%' : heightLocal,
            bottom: 0,
            overflowY: componentToRender === 'TableAnnotationsComponent' ? 'hidden' : 'auto'
          }
        }}
        variant="persistent"
        anchor={isMobile ? 'bottom' : 'left'}
        open={openLeft}
      >
        <DrawerHeader>
          <Grid container width="90%" justifyContent="center">
            <Grid item xs={12}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(auto-fit, minmax(40px, 1fr))`,
                  gap: '8px',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                {isMobile === true ? (
                  <IconButton
                    style={{ color: '#2A3F90' }}
                    aria-label="open drawer"
                    onClick={() => setExpandDrawerMobile(!expandDrawerMobile)}
                    edge="start"
                  >
                    {expandDrawerMobile ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
                  </IconButton>
                ) : (
                  <>
                    {buttonSettings.showAjustarMapa && buttonSettings.showAjustarMapa === true && (
                      <Switch
                        checked={adjustMap}
                        onClick={() => {
                          setCheckAdjustMap(!checkAdjustMap);
                          setAdjustMap(!adjustMap);
                        }}
                        color="warning"
                        sx={{
                          '& .MuiSwitch-switchBase': {
                            color: '#2A3F90',
                            '&.Mui-checked': {
                              color: '#2A3F90'
                            },
                            '&.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#2A3F90',
                              border: '2px solid #2A3F90'
                            }
                          },
                          '& .MuiSwitch-thumb': {
                            border: '2px solid #2A3F90'
                          },
                          '& .MuiSwitch-track': {
                            backgroundColor: '#ffffff',
                            border: '2px solid #2A3F90',
                            opacity: 1
                          }
                        }}
                      />
                    )}
                  </>
                )}
                {orderInitial.map(item => {
                  return (
                    components[item].show &&
                    advancedComponents.includes(item) &&
                    (map.settingsAdvancedMap.showTooltip === true ? (
                      <Tooltip key={uuid()} title={components[item] && components[item].name} arrow>
                        <span key={components[item].name}>
                          <IconButton
                            key={uuid()}
                            onClick={() => {
                              renderComponent(item);
                              tiempoEsperaFunction();
                            }}
                            disabled={tiempoEspera}
                            style={{ color: '#2A3F90' }}
                          >
                            {components[item].icon ? components[item].icon : renderButtons(item)}
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : (
                      <IconButton
                        onClick={() => {
                          renderComponent(item);
                          tiempoEsperaFunction();
                        }}
                        disabled={tiempoEspera}
                        style={{ color: '#2A3F90' }}
                        key={components[item].name}
                      >
                        {components[item].icon ? components[item].icon : renderButtons(item)}
                      </IconButton>
                    ))
                  );
                })}
                {buttonSettings.showChangeSize && buttonSettings.showChangeSize === true && (
                  <>
                    {(isMobile !== true || drawerWidth === 25 || isModalOpen === true) && (
                      <>
                        {map.settingsAdvancedMap.showTooltip === true ? (
                          <>
                            <Tooltip title="Zoom out" arrow>
                              <span>
                                <IconButton
                                  onClick={() => handleDecrement()}
                                  disabled={tiempoEspera}
                                  style={{ color: '#2A3F90', fontSize: 'small' }}
                                >
                                  <RemoveIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Zoom in" arrow>
                              <span>
                                <IconButton
                                  onClick={() => handleIncrement()}
                                  disabled={tiempoEspera}
                                  style={{ color: '#2A3F90', fontSize: 'small' }}
                                >
                                  <AddIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </>
                        ) : (
                          <>
                            <IconButton
                              onClick={() => handleDecrement()}
                              disabled={tiempoEspera}
                              style={{ color: '#2A3F90', fontSize: 'small' }}
                            >
                              <RemoveIcon />
                            </IconButton>
                            <IconButton
                              onClick={() => handleIncrement()}
                              disabled={tiempoEspera}
                              style={{ color: '#2A3F90', fontSize: 'small' }}
                            >
                              <AddIcon />
                            </IconButton>
                          </>
                        )}
                      </>
                    )}
                  </>
                )}
                {(!buttonSettings.templateDefault ||
                  buttonSettings.templateDefault !== 'baseTemplate3') && (
                  <>
                    {(!buttonSettings.showModal || buttonSettings.showModal === true) && (
                      <>
                        {drawerWidth !== 100 && isMobile !== true && (
                          <IconButton
                            className={classes.iconButtonStyle}
                            onClick={() => setDrawerWidth(100)}
                          >
                            <AspectRatioIcon />
                          </IconButton>
                        )}
                      </>
                    )}
                    <IconButton
                      className={classes.iconButtonStyle}
                      onClick={() => {
                        if (isEqual(drawerWidth, 100)) setDrawerWidth(100 / 3);
                        else handleDrawerBottom();
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </>
                )}
              </div>
            </Grid>
          </Grid>
        </DrawerHeader>
        {!isEmpty(componentToRender) ? (
          <Box
            bgcolor="white"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: componentToRender === 'TableAnnotationsComponent' ? '100%' : 'auto',
              overflowY: componentToRender === 'TableAnnotationsComponent' ? 'hidden' : 'auto',
              overflowX: 'hidden'
            }}
          >
            {componentToRender}
          </Box>
        ) : null}
      </Drawer>
    </>
  );
};

export default BotonMenu;

BotonMenu.defaultProps = {
  changeLoading: () => {},
  isMobile: false,
  isPublicMap: false,
  map: {},
  MapProps: {},
  userProps: {}
};
BotonMenu.propTypes = {
  changeLoading: PropTypes.func,
  isMobile: PropTypes.bool,
  isPublicMap: PropTypes.bool,
  map: PropTypes.object,
  MapProps: PropTypes.object,
  userProps: PropTypes.object
};
